// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { AppIcon, Translation } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { UploadDocumentFilesSection } from './upload-document-files-section';
import { UploadDocumentFormFields } from './upload-document-form-fields';
import {
  useUploadDocumentDrawer,
  type UseUploadDocumentDrawerProps,
} from './use-upload-document-drawer';

export type UploadDocumentDrawerProps = UseUploadDocumentDrawerProps;

export const UploadDocumentDrawer = defineComponent({
  displayName: 'UploadDocumentDrawer',
  useSetup: useUploadDocumentDrawer,
  View: ({
    DrawerRenderer,
    onRequestClose,
    t,
    form,
    selectedFiles,
    fileError,
    fileErrorMessage,
    isDragging,
    fileInputRef,
    filesSummaryLabel,
    isSaveDisabled,
    isSubmitting,
    categorySelectOptions,
    subcategorySelectOptions,
    currencySelectOptions,
    languageSelectOptions,
    onFileInputChange,
    onDrop,
    onDragOver,
    onDragLeave,
    onSelectFileClick,
    onRemoveFile,
    onScopeChange,
    onCategoryChange,
    onSaveClick,
    hideBackdrop,
  }) => (
    <DrawerRenderer
      anchor="right"
      PaperProps={{
        classes: {
          root: '!bg-page !w-full !max-w-2xl',
        },
      }}
      data-testid="upload-document-drawer"
      hideBackdrop={hideBackdrop}
    >
      <List dense disablePadding className="!flex !min-h-full !flex-col">
        <ListItem
          className="relative flex flex-col !items-start gap-1 p-7"
          disablePadding
        >
          <div className="absolute right-0 top-0 p-7">
            <IconButton
              icon="Close"
              variant="icon"
              size="md"
              color="main"
              aria-label={t('common.close', { ns: 'common' })}
              onClick={onRequestClose}
              disabled={isSubmitting}
              data-testid="upload-document-close"
            />
          </div>
          <Typography variant="h6" data-testid="upload-document-drawer-title">
            <Translation i18nKey="documents.upload-drawer.title" ns="components" />
          </Typography>
        </ListItem>

        <Divider />

        <ListItem
          className="flex min-h-0 flex-1 flex-col !items-stretch gap-6 overflow-y-auto p-7"
          disablePadding
        >
          <UploadDocumentFormFields
            form={form}
            categorySelectOptions={categorySelectOptions}
            subcategorySelectOptions={subcategorySelectOptions}
            currencySelectOptions={currencySelectOptions}
            languageSelectOptions={languageSelectOptions}
            isSubmitting={isSubmitting}
            onScopeChange={onScopeChange}
            onCategoryChange={onCategoryChange}
            t={t}
          />

          <UploadDocumentFilesSection
            fileInputRef={fileInputRef}
            selectedFiles={selectedFiles}
            isDragging={isDragging}
            fileError={fileError}
            fileErrorMessage={fileErrorMessage}
            filesSummaryLabel={filesSummaryLabel}
            onFileInputChange={onFileInputChange}
            onSelectFileClick={onSelectFileClick}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onRemoveFile={onRemoveFile}
          />
        </ListItem>

        <Divider />

        <ListItem className="flex flex-col gap-2 p-7" disablePadding>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={onSaveClick}
            disabled={isSaveDisabled}
            fullWidth
            className="normal-case"
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AppIcon icon="Upload" color="inherit" size="base" />
              )
            }
            data-testid="upload-document-save"
          >
            <Translation
              i18nKey="documents.upload-drawer.save-document"
              ns="components"
            />
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={onRequestClose}
            disabled={isSubmitting}
            fullWidth
            className="normal-case"
            data-testid="upload-document-cancel"
          >
            <Translation i18nKey="common.cancel" ns="common" />
          </Button>
        </ListItem>
      </List>
    </DrawerRenderer>
  ),
});
